import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import Editor from '@monaco-editor/react'
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api'
import useResizeObserver from '@react-hook/resize-observer'

export interface ICodeEditor {
  /** Value to display/edit in the editor. */
  value?: string

  /** Default value to display/edit in the editor. */
  defaultValue?: string

  /** Height of the editor.
   *
   * @default "100%""
   */
  height?: string

  /** Width of the editor.
   *
   * @default "100%"
   */
  width?: string

  /** Minimum height of the editor.
   *
   * @default "100px"
   */
  minHeight?: string

  /** Minimum width of the editor.
   *
   * @default "100px""
   */
  minWidth?: string

  /** Language of the current model (for syntax highlighting, etc.) */
  language?: string

  /** The theme for the editor.
   *
   * @default "light"
   */
  theme?: 'light' | 'vs-dark'

  /** Options controlling the behavior of the editor
   * (see https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.IEditorConstructionOptions.html
   * for a full list of options).
   */
  options?: monaco.editor.IStandaloneEditorConstructionOptions | undefined

  /** Callback when value changes. */
  onChange?: (value: string | undefined) => unknown

  /** Callback for keyDown-events. */
  onKeyDown?: (event: monaco.IKeyboardEvent) => unknown
}

const systemJsDefine = window.define

export const RawCodeEditor = React.forwardRef<HTMLDivElement, ICodeEditor>(
  (
    {
      language,
      options,
      defaultValue,
      value,
      width = '100%',
      height = '100%',
      minHeight = '100px',
      minWidth = '100px',
      theme = 'light',
      onChange,
      onKeyDown,
    },
    ref
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null)
    const keyDownRef = useRef<(event: monaco.IKeyboardEvent) => void>()

    useImperativeHandle(ref, () => wrapperRef.current as HTMLDivElement)

    const resizeEditor = useCallback(() => {
      const rect = wrapperRef.current?.getBoundingClientRect()
      if (!rect || !editorRef.current) return

      const width = rect.width
      const height = rect.height

      editorRef.current.layout({ width, height })
    }, [])

    useResizeObserver(wrapperRef, resizeEditor)

    const handleMount = useCallback(
      (editor: monaco.editor.IStandaloneCodeEditor) => {
        if (systemJsDefine) {
          window.define = systemJsDefine
        }

        editor.onKeyDown((e) => keyDownRef.current?.(e))

        editorRef.current = editor
        resizeEditor()
      },
      []
    )

    useEffect(() => {
      keyDownRef.current = onKeyDown
    }, [onKeyDown])

    return (
      <div
        style={{ height, width, minHeight, minWidth, overflow: 'hidden' }}
        ref={wrapperRef}
      >
        <Editor
          language={language}
          defaultValue={defaultValue}
          value={value}
          theme={theme}
          options={options}
          onMount={handleMount}
          onChange={onChange}
        />
      </div>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  RawCodeEditor.displayName = 'CodeEditor'
}

export const CodeEditor = React.memo(RawCodeEditor)
